import { Users, Download, Building2, UserCheck, Gauge } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Callout, RoleBadge, pathFor,
  Diagram, MockTabs, MockSelect, MockField, MockButton, MockKpi, MockTable, MockRow,
} from "../shared";

export default function PagesHrDossierSection() {
  return (
    <div data-testid="guide-pages-hr-dossier">
      <PageHeading
        icon={Users}
        title="HR Dossier"
        description="One place to answer fair, people-focused questions: who is sitting on late work right now, how each person has performed over time, how a whole department is doing, and how the senior-review tier handled what was sent up to it."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="hr_admin" />
        <span className="text-xs text-white/40">HR Admin and above. Some tabs need extra permissions — noted on each tab below.</span>
      </div>

      <Callout variant="info" title="In plain words">
        The HR Dossier is a set of <span className="text-white">report screens</span>. You don't change anything here — you pick what you want to look at, press a button, and read the results. There are four tabs across the top, each answering a different question.
      </Callout>

      <Section title="Finding the four tabs">
        <p className="text-sm text-white/65 leading-relaxed">
          Open <span className="text-white">HR Dossier</span> from the top menu. Along the top of the page you'll see four tabs. Click a tab to switch between the reports.
        </p>
        <Diagram
          frameLabel="HR Dossier"
          legend={[
            <><span className="text-white">Overdue Dossier</span> — who is holding late work right now.</>,
            <><span className="text-white">MD Review History</span> — how the senior-review tier handled items sent up.</>,
            <><span className="text-white">Employee Dossier</span> — one person's full track record, plus what they're holding today.</>,
            <><span className="text-white">Department Dossier</span> — a whole team's scorecard on one screen.</>,
          ]}
        >
          <MockTabs
            tabs={[
              { label: "Overdue Dossier", active: true, marker: 1 },
              { label: "MD Review History", marker: 2 },
              { label: "Employee Dossier", marker: 3 },
              { label: "Department Dossier", marker: 4 },
            ]}
          />
        </Diagram>
      </Section>

      <Section title="Tab 1 — Overdue Dossier">
        <p className="text-sm text-white/65 leading-relaxed">
          Answers <span className="text-white">"who is sitting on overdue work right now?"</span> It lists every action item past its due date, grouped by the person <em>currently holding it</em> (not the person it was first given to).
        </p>
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2 mt-2">
          <li>One card per person, sorted so whoever has the most overdue items is at the top.</li>
          <li>Each item shows its title, the original owner, the due date, and how many days late it is.</li>
          <li>Finished or sent-up work (Closed, Cancelled, Sent Upward) is left out — this is only about live, late work.</li>
        </ul>
        <Diagram
          frameLabel="HR Dossier → Overdue Dossier"
          legend={[
            <>Type a name or keyword here to filter the list.</>,
            <>Each card is one person, with their total number of overdue items.</>,
            <>If you can use the Employee Dossier, click a person's name to jump straight to their full record.</>,
          ]}
        >
          <MockField marker={1} placeholder="Filter by holder, item, lane, or assignee…" />
          <div className="rounded-md border border-white/10 bg-white/[0.03] p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-2 text-sm text-white/80">
                <UserCheck className="w-4 h-4 text-cyan-300" /> Amal R. <span className="text-2xs text-white/40">(currently holding)</span>
              </span>
              <span className="text-xs text-red-300">3 overdue</span>
            </div>
            <MockTable
              columns={["Item", "Original owner", "Due", "Days late"]}
              rows={[
                ["Q3 portfolio pack", "Sara K.", "12 May", <span className="text-red-300">+9</span>],
                ["IC minutes follow-up", "Amal R.", "20 May", <span className="text-red-300">+1</span>],
              ]}
            />
            <div className="mt-2"><MockButton label="Open in Employee Dossier" marker={3} variant="ghost" /></div>
          </div>
        </Diagram>
        <Callout variant="tip">
          Use this every morning to see, at a glance, where late work is parked — without filtering the Updates page by hand.
        </Callout>
      </Section>

      <Section title="Tab 2 — MD Review History">
        <p className="text-sm text-white/65 leading-relaxed">
          Answers <span className="text-white">"how did the senior-review tier handle the things we sent up?"</span> Any waiting time here is counted against the <span className="text-white">review tier</span>, not the person who did the original work — so nobody is blamed for a delay that happened above them.
        </p>
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2 mt-2">
          <li>For each item: when it was sent up, the original date, any new dates it was pushed to, and where it ended up.</li>
          <li>A coloured pill shows whether the holding time stayed within target — <span className="text-emerald-300">green</span> (on target), <span className="text-amber-300">amber</span> (slightly over), <span className="text-red-300">red</span> (well over). The default target is 10 working days; a Super Admin can change it.</li>
          <li>Filters let you narrow by date range, colour, original owner, or current status.</li>
        </ul>
        <Diagram
          frameLabel="HR Dossier → MD Review History"
          legend={[
            <>Set the filters (date range, colour, owner, status) to narrow the list.</>,
            <>Read the coloured pill to see if each item stayed within the holding-time target.</>,
            <>Click <span className="text-white">Download PDF</span> to save exactly what's on screen — handy for "only red items from last quarter" board packs.</>,
          ]}
        >
          <MockRow>
            <MockSelect label="SLA verdict" value="Red only" marker={1} />
            <MockSelect label="Original owner" value="All owners" />
            <MockSelect label="Status" value="All statuses" />
          </MockRow>
          <MockTable
            columns={["Item", "Sent up → ended", "Held (working days)", "Verdict"]}
            rows={[
              ["Board resolution 14", "2 Apr → Closed", "13", <span className="text-red-300">● Red</span>],
              ["ExCom paper", "9 Apr → Open", "7", <span className="text-emerald-300">● Green</span>],
            ]}
          />
          <MockButton label="Download PDF" marker={3} variant="ghost" />
        </Diagram>
      </Section>

      <Section title="Tab 3 — Employee Dossier">
        <div className="flex items-center gap-2 mb-2">
          <RoleBadge role="hr_admin" />
          <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">view_employee_dossier</span> permission.</span>
        </div>
        <p className="text-sm text-white/65 leading-relaxed">
          Answers <span className="text-white">"how has this one person performed, and what are they carrying right now?"</span> Pick a person and a date range, and the screen builds their full track record. The clever part: when work was handed over, returned for changes, or reassigned, the time is split fairly between each holder — so people are only judged on the stretch they actually held the item.
        </p>

        <p className="text-sm text-white/65 leading-relaxed mt-3">
          <span className="inline-flex items-center gap-1.5 text-white"><Gauge className="w-4 h-4 text-cyan-300" /> Workload now</span> — as soon as you pick a person (before you even press Generate), a live snapshot appears showing what they're holding <em>today</em>: how many items they currently hold, how many they own, how many they've received from other people, how many are overdue, and a breakdown by status.
        </p>

        <Diagram
          frameLabel="HR Dossier → Employee Dossier"
          legend={[
            <>Pick the person you want to look at.</>,
            <>Choose a date range — or click a quick range like "Last 90 days".</>,
            <>The <span className="text-white">Workload now</span> snapshot appears straight away, before you generate anything.</>,
            <>Press <span className="text-white">Generate</span> to build the full historical track record below.</>,
          ]}
        >
          <MockRow>
            <MockSelect label="Person" value="Amal R." marker={1} />
            <MockField label="From" value="01 Jan 2026" marker={2} />
            <MockField label="To" value="20 Jun 2026" />
            <MockButton label="Generate" marker={4} />
          </MockRow>
          <div className="rounded-md border border-cyan-400/25 bg-cyan-500/[0.04] p-3">
            <p className="text-2xs uppercase tracking-wider text-cyan-300/80 mb-2 font-semibold flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5" /> Workload now — Amal R.
            </p>
            <MockRow>
              <MockKpi label="Holding" value="6" marker={3} />
              <MockKpi label="Owns" value="4" />
              <MockKpi label="From others" value="2" />
              <MockKpi label="Overdue" value="3" tone="bad" />
            </MockRow>
          </div>
        </Diagram>
        <Callout variant="tip">
          You can also reach this tab by clicking a person's name on the Overdue Dossier tab — it opens their Employee Dossier already filled in.
        </Callout>
      </Section>

      <Section title="Tab 4 — Department Dossier">
        <div className="flex items-center gap-2 mb-2">
          <RoleBadge role="hr_admin" />
          <span className="text-xs text-white/40">Needs the <span className="font-mono text-white/70">view_employee_dossier</span> permission.</span>
        </div>
        <p className="text-sm text-white/65 leading-relaxed">
          Answers <span className="text-white">"how is this whole team doing?"</span> Pick a department and a date range, press Generate, and you get one combined scorecard for everyone active in that department.
        </p>
        <Diagram
          frameLabel="HR Dossier → Department Dossier"
          legend={[
            <>Pick the department.</>,
            <>Choose a date range (or a quick range).</>,
            <>Press <span className="text-white">Generate</span>.</>,
            <>Read the headline <span className="text-white">on-time rate</span> band for the whole team.</>,
            <>Below it, a <span className="text-white">Workload now</span> band shows what the team is holding today.</>,
            <>The <span className="text-white">league table</span> lists each person, sorted so the most-overdue surface at the top.</>,
          ]}
        >
          <MockRow>
            <MockSelect label="Department" value="Investments" marker={1} />
            <MockField label="From" value="01 Jan 2026" marker={2} />
            <MockField label="To" value="20 Jun 2026" />
            <MockButton label="Generate" marker={3} />
          </MockRow>
          <MockRow>
            <MockKpi label="On-time rate" value="82%" marker={4} tone="good" />
            <MockKpi label="Touched" value="48" />
            <MockKpi label="On time" value="39" tone="good" />
            <MockKpi label="Late" value="9" tone="warn" />
          </MockRow>
          <div className="rounded-md border border-cyan-400/25 bg-cyan-500/[0.04] p-3">
            <p className="text-2xs uppercase tracking-wider text-cyan-300/80 mb-2 font-semibold flex items-center gap-1.5">
              <Gauge className="w-3.5 h-3.5" /> Workload now — whole department
            </p>
            <MockRow>
              <MockKpi label="Holding" value="21" marker={5} />
              <MockKpi label="Overdue" value="5" tone="bad" />
              <MockKpi label="From outside" value="3" />
            </MockRow>
          </div>
          <MockTable
            columns={[<span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />Person</span>, "On-time", "Holding", "Overdue"]}
            rows={[
              ["Sara K.", <span className="text-emerald-300">95%</span>, "5", "0"],
              ["Amal R.", <span className="text-amber-300">78%</span>, "6", <span className="text-red-300">3</span>],
              ["Ravi P.", <span className="text-emerald-300">88%</span>, "4", "1"],
            ]}
          />
        </Diagram>
        <Callout variant="info">
          The on-time rate is clean conclusions ÷ (clean + late + overdue) over the range you chose. A task touched by several people in the department is only counted once for the department total, so the team figure is never double-counted.
        </Callout>
      </Section>

      <Callout variant="info" title="Where this lives in the menu">
        The HR Dossier link only appears for users whose role grants <span className="font-mono text-white">view_hr_dossier</span>. The Employee and Department Dossier tabs additionally need <span className="font-mono text-white">view_employee_dossier</span>. See <Link href={pathFor("roles")} className="text-cyan-300 hover:underline">Roles &amp; Access</Link> for how a Super Admin grants these.
      </Callout>

      <Callout variant="tip" title="Download for sharing">
        The <span className="inline-flex items-center gap-1"><Download className="w-3 h-3" /> Download</span> buttons export what's currently on screen, so set your filters first, then download a focused report to attach to a board pack or email.
      </Callout>
    </div>
  );
}
